import type { AbiParameter } from 'viem'
import {
  decodeAbiParameters,
  encodeAbiParameters,
  isAddress as isEVMAddress,
  parseAbiParameters,
} from 'viem'
import type { ConstructorArgument, ConstructorArgumentOption } from 'components/AbiEncoder/types'

const BYTECODE_CHUNK_SIZE = 64 // 32 bytes in hex is 64 characters

enum ABI_INPUT_TYPE {
  String = 'string',
  Address = 'address',
  Boolean = 'boolean',
}

enum VALIDATE_ERRORS {
  NotArray = 'Invalid input: Value is not an array.',
  NotBoolean = 'Invalid input: boolean must be true or false',
  NotAddress = 'Invalid input: value is not an address',
}

const isArrayType = (type: string) => type.includes('[]')

const isBooleanType = (type: string) => type.toLowerCase().includes('bool')

export const getInputPlaceholder = (type: string) => {
  const isArray = isArrayType(type)
  const isBoolean = isBooleanType(type)
  const isAddress = type.toLowerCase().includes('address')
  const isUint = type.toLowerCase().includes('uint')
  const isBytes = type.toLowerCase().includes('bytes')

  let exampleText = ''

  if (isArray) {
    if (isUint) {
      exampleText = '[0,1,2]'
    } else if (isBytes) {
      exampleText = '["0x00","0x11","0x22"]'
    } else if (isBoolean) {
      exampleText = '[true, false, false]'
    } else if (isAddress) {
      exampleText = `["0x11...11", "0x21...21", "0x31...31"]`
    } else {
      exampleText = `["str0", "str1", "str2"]`
    }
  } else if (isUint) {
    exampleText = '111222333'
  } else if (isBytes) {
    exampleText = '0x112233'
  } else if (isBoolean) {
    exampleText = 'false'
  } else if (isAddress) {
    exampleText = `0x11...11`
  } else {
    exampleText = `str0`
  }

  return `Example: ${exampleText}`
}

type Validator = (value: string) => string | null

export const validateArray = ({ value, validator }: { value: string; validator: Validator }) => {
  try {
    const parsedValue = JSON.parse(value)

    if (!Array.isArray(parsedValue)) {
      return VALIDATE_ERRORS.NotArray
    }

    let validatorErrorMessage: string | null = null

    parsedValue.forEach((item: string | boolean) => {
      if (validatorErrorMessage) {
        return
      }

      if (typeof item !== 'string') {
        // eslint-disable-next-line no-param-reassign
        item = item.toString()
      }

      validatorErrorMessage = validator(item)
    })

    return validatorErrorMessage
  } catch (error) {
    if (error instanceof Error) {
      return error.message
    }

    return null
  }
}

const validators: { [x: string]: Validator } = {
  [ABI_INPUT_TYPE.Address]: (value: string) =>
    isEVMAddress(value) ? null : VALIDATE_ERRORS.NotAddress,
  'address[]': (value: string) =>
    validateArray({ value, validator: validators[ABI_INPUT_TYPE.Address] }),
  [ABI_INPUT_TYPE.Boolean]: (value: string) => {
    if (!['true', 'false'].includes(value)) {
      return VALIDATE_ERRORS.NotBoolean
    }
    return null
  },
  'bool[]': (value: string) =>
    validateArray({ value, validator: validators[ABI_INPUT_TYPE.Boolean] }),
}

export const encodeArguments = (constructorArguments: ConstructorArgument[]) => {
  if (constructorArguments.length === 0) {
    return { argumentErrors: [], encodedArguments: '' }
  }

  const argumentInputs = constructorArguments
  const inputsLength = constructorArguments.length
  const types: string[] = []
  const inputs: (string | boolean)[] = []
  const argumentErrors: string[] = []
  const emptyLines = argumentInputs.filter((input) => !input.value).length
  const booleanParser = (value: string) => value === 'true'

  let encodedArguments = ''
  let areArgumentsValid = true

  if (emptyLines === inputsLength && inputsLength > 0) {
    areArgumentsValid = false

    return {
      argumentErrors,
      encodedArguments,
    }
  }

  argumentInputs.forEach(({ value, type }: ConstructorArgument, index) => {
    const isArray = isArrayType(type)
    const isBoolean = isBooleanType(type)

    const validator: Validator = validators[type] || (() => '')
    types.push(type)
    let errorMessage = validator(value)

    if (!errorMessage && isArray) {
      errorMessage = validateArray({ value, validator: () => '' })
    }

    try {
      if (errorMessage) {
        areArgumentsValid = false
        argumentErrors[index] = errorMessage
        return
      }

      if (isArray && value) {
        inputs.push(JSON.parse(value) as string)
      } else if (isBoolean) {
        inputs.push(booleanParser(value))
      } else {
        inputs.push(value)
      }

      encodeAbiParameters(parseAbiParameters(types.join(', ')), inputs)
      argumentErrors[index] = ''
    } catch (error) {
      if (error instanceof Error) {
        areArgumentsValid = false
        argumentErrors[index] = `Invalid input: ${error.message}`
      }
    }
  })

  if (areArgumentsValid) {
    if (inputs.length > 0) {
      encodedArguments += encodeAbiParameters(parseAbiParameters(types.join(', ')), inputs).slice(2)
    }

    return {
      argumentErrors,
      encodedArguments,
    }
  }

  return {
    argumentErrors,
    encodedArguments,
  }
}

interface ConstructorAbi {
  type: 'constructor'
  inputs: ReadonlyArray<AbiParameter>
}

export const decodeArguments = (abi: string, constructorArgumentsBytecode?: string | null) => {
  if (!constructorArgumentsBytecode) {
    return null
  }

  const constructorAbi = JSON.parse(abi).find(
    (item: object): item is ConstructorAbi => 'type' in item && item.type === 'constructor'
  )

  // Try to remove bytecode chunks from the starting from the beginning to handle edge case
  // where occasionally there is an additional bytecode included in the beginning which causes the decoder to crash.
  let constructorArguments = constructorArgumentsBytecode
  let abiDecodedConstructorArgs = null

  // FIXME: Hotfix for edge case when bytecode isnt a multiple of 64 and it crashes the app during decoding on line 249.
  // Find out why it does so even if it's wrapped in a trycatch.
  const bytecodeChunkModulo = constructorArguments.length % BYTECODE_CHUNK_SIZE

  if (bytecodeChunkModulo !== 0) {
    constructorArguments = constructorArguments.slice(bytecodeChunkModulo)
  }

  while (constructorArguments.length >= BYTECODE_CHUNK_SIZE) {
    if (abiDecodedConstructorArgs) {
      break
    }

    try {
      const decodedChunk = decodeAbiParameters(constructorAbi.inputs, `0x${constructorArguments}`)

      abiDecodedConstructorArgs = decodedChunk
    } catch {
      // TODO: Potentially add logging to sentry with a tag after we set it up with Balman.
      constructorArguments = constructorArguments.slice(BYTECODE_CHUNK_SIZE)
    }
  }

  if (!abiDecodedConstructorArgs) {
    console.warn(
      'Constructor Arguments: Unable to decode constructor arguments after all attempts.'
    )
    return null
  }

  const encodedArgs = []

  for (let index = 0; index < constructorArguments.length; index += BYTECODE_CHUNK_SIZE) {
    encodedArgs.push(constructorArguments.slice(index, index + BYTECODE_CHUNK_SIZE))
  }

  const decodedArgs = constructorAbi.inputs.map(
    (input: AbiParameter & { value: string }, index: number) => ({
      type: input.type,
      name: input.name,
      value: abiDecodedConstructorArgs[index],
    })
  )

  return { decodedArgs, encodedArgs, constructorArguments }
}

export const generateNumerableTypeOptions = (
  type: string,
  label: string,
  max: number,
  step: number
) => {
  const options: ConstructorArgumentOption[] = []

  for (let index = 0; index <= max; index += step) {
    if (index === 0) {
      options.push({ value: type, label })
      options.push({ value: `${type}[]`, label: `${label}[]` })
    } else {
      options.push({ value: `${type}${index}`, label: `${label}${index}` })
      options.push({ value: `${type}${index}[]`, label: `${label}${index}[]` })
    }
  }

  return options
}

const generateUintOptions = () => generateNumerableTypeOptions('uint', 'Uint', 256, 8)

const generateBytesOptions = () => generateNumerableTypeOptions('bytes', 'Bytes', 32, 1)

export const argumentOptions = [
  { value: 'address', label: 'Address' },
  { value: 'address[]', label: 'Address[]' },
  { value: 'string', label: 'String' },
  { value: 'bool', label: 'Bool' },
  { value: 'bool[]', label: 'Bool[]' },
  ...generateUintOptions(),
  ...generateBytesOptions(),
]
